import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../lib/auth-context';
import { useSiteSettings, useOrderedCol, useProgress, useDoc } from '../lib/hooks';
import { useCurrentCourse } from '../lib/course';
import { useTheme } from '../lib/theme';
import { Modal, Toaster } from './ui';
import type { CourseDoc } from '../lib/data-model';
import { markActiveToday } from '../lib/hooks';
import { Welcome, GUEST_ENTRY_KEY } from './Welcome';
import { TutorialTour } from './TutorialTour';
import { OnboardingScreen } from '../pages/OnboardingScreen';
import { PomodoroWidget, PomodoroTool } from './PomodoroWidget';
import { LogoMark } from './LogoMark';

export function Layout() {
  const auth = useAuthCtx();
  const uDoc = useDoc<{ name?: string; onboardedAt?: number }>('users', auth.user?.uid ?? '', auth.isSignedIn);
  const [entered] = useState(() => localStorage.getItem(GUEST_ENTRY_KEY) === '1');
  const [settled, setSettled] = useState(false);
  if (!auth.isSignedIn && !entered) return <Welcome />;
  if (auth.isSignedIn && uDoc.loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span className="spinner" aria-label="Loading" />
      </div>
    );
  }
  if (auth.isSignedIn && !uDoc.data?.onboardedAt && !settled) {
    return <OnboardingScreen onDone={() => setSettled(true)} />;
  }
  return (
    <div className="app-shell">
      <div className="orbs" aria-hidden>
        <div className="orb g" />
        <div className="orb p" />
        <div className="orb a" />
        <div className="orb b" />
        <div className="orb i" />
      </div>
      <a className="skip-link" href="#main">Skip to content</a>
      <Header />
      <main id="main" className="page" style={{ flex: 1 }}>
        <div className="container">
          <Outlet />
        </div>
      </main>
      <Footer />
      <Toaster />
      <PomodoroWidget />
      <PomodoroTool />
      <TutorialTour />
    </div>
  );
}

function Header() {
  const auth = useAuthCtx();
  const { data: settings } = useSiteSettings();
  const [theme, toggleTheme] = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const { data: courses } = useOrderedCol<CourseDoc>('courses');
  const [courseKey, setCourseKey] = useCurrentCourse();
  const location = useLocation();
  const navigate = useNavigate();
  const progress = useProgress(auth.isSignedIn);
  const gamified = settings?.gamified !== false;

  useEffect(() => {
    markActiveToday();
  }, [location.pathname]);

  const uDoc = useDoc<{ name?: string }>('users', auth.user?.uid ?? '', auth.isSignedIn);
  const course = courses.find(c => c.id === courseKey) ?? courses[0];
  const initials = (uDoc.data?.name || auth.user?.name || auth.user?.email || '?')
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const nav = (
    <>
      <NavLink to="/" end data-tour="chapters">Chapters</NavLink>
      <NavLink to="/tools" data-tour="tools">Tools</NavLink>
      <NavLink to="/ai" data-tour="ai">AI</NavLink>
      {auth.isAdmin && <NavLink to="/admin">Admin</NavLink>}
    </>
  );

  return (
    <header className="site-header">
      <div className="container hbar">
        <button className="brand" onClick={() => navigate('/')} aria-label="Go to home">
          <span className="logo" aria-hidden><LogoMark /></span>
          <span className="name">{settings?.appName ?? 'MasterStax'}</span>
        </button>

        {course && (
          <button className="course-chip" data-tour="course" onClick={() => setPickerOpen(true)} title="Switch course">
            <span className="dot" style={{ background: course.accent }} aria-hidden />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{course.name}</span>
            <span className="mono muted" style={{ fontSize: 11, flex: 'none' }} aria-hidden>⇄</span>
          </button>
        )}

        <nav className="header-nav" aria-label="Primary">
          {nav}
        </nav>

        <div className="header-right">
          {gamified && auth.isSignedIn && (
            <>
              <span className="streak-pill" data-tour="streak" title="Your study streak">
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)' }} aria-hidden />
                {progress.streak} day{progress.streak === 1 ? '' : 's'}
              </span>
              <span className="xp-pill" title="Experience points">{progress.xp} XP</span>
            </>
          )}
          <button className="icon-btn" data-tour="theme" onClick={toggleTheme} title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}>
            <span aria-hidden>{theme === 'dark' ? '☀' : '☾'}</span>
          </button>
          {auth.isSignedIn ? (
            <NavLink to="/profile" className="avatar-btn" data-tour="profile" title="Your profile" aria-label="Your profile">
              {initials || '?'}
            </NavLink>
          ) : (
            <button className="btn btn-dark btn-sm" onClick={() => void auth.signInWithGoogle()}>
              Sign in
            </button>
          )}
        </div>
      </div>

      {pickerOpen && (
        <CoursePicker courses={courses} currentId={course?.id} onClose={() => setPickerOpen(false)} onPick={id => { setCourseKey(id); setPickerOpen(false); navigate('/'); }} />
      )}
    </header>
  );
}

function CoursePicker({ courses, currentId, onClose, onPick }: { courses: Array<CourseDoc & { id: string }>; currentId?: string; onClose: () => void; onPick: (id: string) => void }) {
  return (
    <Modal title="Switch course" onClose={onClose}>
      <div style={{ display: 'grid', gap: 12 }}>
        {courses.map(c => {
          const active = c.id === currentId;
          return (
            <button key={c.id} className="admin-item course-pick" data-active={active || undefined} onClick={() => onPick(c.id)} style={{ textAlign: 'left', borderColor: active ? 'var(--brand)' : undefined }}>
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: c.accent, flex: 'none' }} aria-hidden />
              <span className="t">
                {c.name}
                <small>{c.code}</small>
              </span>
              {active && <span className="mono" style={{ color: 'var(--c-green)' }} aria-hidden>✓</span>}
            </button>
          );
        })}
        {courses.length === 0 && <p className="muted">No courses yet.</p>}
      </div>
    </Modal>
  );
}

function Footer() {
  const { data: settings } = useSiteSettings();
  return (
    <footer className="site-footer">
      <div className="container fbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
          <span className="logo" style={{ width: 28, height: 28, borderRadius: 9, background: 'var(--brand)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }} aria-hidden>
            <LogoMark />
          </span>
          <span style={{ fontSize: 14, color: 'var(--ink2)' }}>{settings?.footerText ?? '© 2026 MasterStax.'}</span>
        </div>
        <nav className="meta" aria-label="Legal">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/terms">Terms of Service</Link>
          <span>Course content built on OpenStax</span>
          <a href="https://mikalabs.org" target="_blank" rel="noopener noreferrer">
            A MikaLabs product
          </a>
        </nav>
      </div>
    </footer>
  );
}
